import React, { useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";

import AddIcon from "@mui/icons-material/Add";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { Button, Stack, Typography } from "@mui/material";
import EmptyCardDealWorkspace from "assets/images/EmptyCardDealWorkspace.png";
import { WorkspaceDocumentIcon, WorkspaceFolderCloseIcon, WorkspaceFormIcon } from "assets/material-custom-icons";
import { ColumnVisibilityMenu } from "components/Deal-workspace/Menus/ColumnVisibilityMenu";
import { DocumentActions, Props as DocumentActionsProps } from "components/Deal-workspace/Menus/DocumentActions";
import { FileActions, Props as FileActionsProps } from "components/Deal-workspace/Menus/FileActions";
import { FolderActions, Props as FolderActionsProps } from "components/Deal-workspace/Menus/FolderActions";
import { FormActions, Props as FormActionsProps } from "components/Deal-workspace/Menus/FormActions";
import { MetadataTable } from "components/Deal-workspace/Workspace-metadata-components/MetadataTable";
import { Share } from "components/Deal-workspace/Workspace-metadata-components/Share";
import { EmptyCard } from "components/Empty-card/EmptyCard";
import { DealRoleGuard } from "components/guards";
import { Menu } from "components/popups";
import { EMPTY_CARD_CONTENT } from "constants/empty-card";
import { EntityType, RoleName } from "constants/index";
import { Routes } from "constants/routes";
import { useHighlightAfterClose } from "hooks";
import { usePickUserPermissions } from "store";
import { FolderKind, IFolder, UserPermissionType } from "store/types";

import { useFolderHook } from "../Deal-workspace/Hooks/use-folder-hook";
import { useWorkspaceFilter } from "../Deal-workspace/Hooks/use-workspace-filter";
import { BulkActionsBar } from "../Deal-workspace/Workspace-metadata-components/BulkActionsBar";
import { WorkspaceFilter } from "../Deal-workspace/Workspace-metadata-components/WorkspaceFilter";
import { useBulkFileActionsHook } from "./hooks/use-bulk-file-actions-hook";
import { useWorkspaceBulkActionsHook } from "./hooks/use-workspace-bulk-actions-hook";
import { useWorkspaceCellHook } from "./hooks/use-workspace-cell-hook";
import { useWorkspaceDocumentHook } from "./hooks/use-workspace-document-hook";
import { useWorkspaceFileHook } from "./hooks/use-workspace-file-hook";
import { useWorkspaceFolderHook } from "./hooks/use-workspace-folder-hook";
import { useWorkspaceFormHook } from "./hooks/use-workspace-form-hook";
import { Breadcrumbs } from "./Metadata/Breadcrubms";
import { useWorkspace } from "./WorkspaceContext";

/** Returns path from root to the folder with targetId (inclusive), using parent_id. */
function getFolderPathFromRoot(folders: IFolder[], targetId: number): IFolder[] {
  const byId = new Map(folders.map((f) => [f.id, f]));
  const path: IFolder[] = [];
  let id: number | null = targetId;
  while (id != null) {
    const folder = byId.get(id);
    if (!folder) break;
    path.push(folder);
    id = folder.parent_id;
  }
  path.reverse();
  return path;
}

const WorkspaceFolderActions: React.FC<FolderActionsProps> = (props) => (
  <FolderActions {...props} useHook={useWorkspaceFolderHook} />
);

const WorkspaceFormActions: React.FC<FormActionsProps> = (props) => (
  <FormActions {...props} useHook={useWorkspaceFormHook} />
);

const WorkspaceDocumentActions: React.FC<DocumentActionsProps> = (props) => (
  <DocumentActions {...props} useHook={useWorkspaceDocumentHook} />
);

const WorkspaceFileActions: React.FC<FileActionsProps> = (props) => (
  <FileActions {...props} useHook={useWorkspaceFileHook} />
);

export const Folder: React.FC = () => {
  const { deal_id: dealIdParam, folder_id: folderIdParam } = useParams<{ deal_id: string; folder_id: string }>();
  const dealId = dealIdParam ? Number(dealIdParam) : null;
  const folderId = folderIdParam ? Number(folderIdParam) : null;
  const {
    deals,
    foldersByDeal,
    currentFolder,
    activeFilters,
    filtersUserInteracted,
    getFolderMetadata,
    folderMetadataLoading: isLoading,
    folderMetadata: metadata,
    handleChangeFilter,
    globalFolderFilters,
    columnVisibility,
    columnOrder,
    reorderColumns,
    selectedItems,
    setSelectedItems,
    toggleColumn,
    refetchFolderMetadata,
    handleRowClick,
    globalFolders,
    handleFetchNextPage,
    sortBy,
    setSortBy,
    globalFolderFiltersLoading,
    dispatchDetail
  } = useWorkspace();

  const { filtersList, dataroomOnlyKeys, isDataroom, isAggregateView, filterTypes } = useWorkspaceFilter({
    currentFolder,
    workspaceFiltersFromDB: globalFolderFilters
  });

  const { handleBulkDelete, handleBulkDownload } = useWorkspaceBulkActionsHook({
    selectedItems,
    setSelectedItems,
    useItemDealId: true
  });

  const { highlightedId, lastViewedIdRef: lastViewedFileIdRef, triggerHighlight } = useHighlightAfterClose();

  const {
    handleBulkView,
    handleBulkExtract,
    handleChangeClassification,
    handleChangeCurrency,
    handleChangeInvestor,
    handleChangeEffectiveDate,
    handleChangeStatus
  } = useBulkFileActionsHook({
    source: "deal",
    dealId,
    selectedItems,
    updateFile: (fileId, patch) => {
      dispatchDetail({
        key: "patch",
        id: fileId,
        entityType: EntityType.FILE,
        patch
      });
    },
    lastViewedFileIdRef,
    triggerHighlight,
    showErrors: false
  });

  const myDeals = usePickUserPermissions("deals");
  const noAccessPermission = myDeals && myDeals === UserPermissionType.NO_ACCESS;
  const isViewerPermission = myDeals && myDeals === UserPermissionType.VIEWER;

  const { onCreateNewFolder, onFileUpload, onAddForm, onAddDocument } = useFolderHook({
    folder: {
      id: currentFolder?.id || -1,
      title: currentFolder?.title || "",
      is_private: currentFolder?.is_private || false,
      parent_id: currentFolder?.parent_id || null,
      deal_id: currentFolder?.deal_id || null,
      user_id: currentFolder?.user_id || -1,
      kind: currentFolder?.kind || "folder"
    },
    refetchHandler: refetchFolderMetadata
  });

  const deal = useMemo(() => (dealId != null ? deals.find((d) => d.id === dealId) : null), [deals, dealId]);
  const dealFolders = useMemo(() => (dealId != null ? (foldersByDeal.get(dealId) ?? []) : []), [foldersByDeal, dealId]);
  const folderPath = useMemo(() => {
    if (folderId == null) return [];
    const folders = dealId != null ? dealFolders : globalFolders;
    return getFolderPathFromRoot(folders, folderId);
  }, [dealFolders, globalFolders, folderId, dealId]);

  useEffect(() => {
    setSelectedItems([]);
    getFolderMetadata();
  }, [dealId, folderId, activeFilters, sortBy]);

  if (folderId == null) {
    return null;
  }

  const dealTitle = deal?.title ?? "Deal";
  const folderTitle = currentFolder?.title ?? "Folder";
  const details = metadata?.details ?? [];
  const kind = metadata?.kind ?? "folder";

  const breadcrumbLinks = useMemo(() => {
    const toFolderPath = (id: number) =>
      dealId != null ? `${Routes.workspace}/deal/${dealId}/folder/${id}` : `${Routes.workspace}/folder/${id}`;

    const base: Array<{ path: string; title: string }> =
      dealId != null
        ? [
            { path: Routes.workspaceDeals, title: "Workspace" },
            { path: Routes.workspaceDeals, title: "Deals" },
            { path: `${Routes.workspace}/deal/${dealId}`, title: dealTitle }
          ]
        : [
            { path: Routes.workspaceFolders, title: "Workspace" },
            { path: Routes.workspaceFolders, title: "Folders" }
          ];

    const folderLinks = folderPath.map((folder) => ({
      path: toFolderPath(folder.id),
      title: folder.title
    }));
    if (folderPath.length === 0 && folderId != null) {
      folderLinks.push({ path: toFolderPath(folderId), title: folderTitle });
    }

    return [...base, ...folderLinks];
  }, [dealId, dealTitle, folderPath, folderId, folderTitle]);

  return (
    <Stack
      direction="column"
      sx={{
        width: "100%",
        flex: 1,
        minHeight: 0,
        overflow: "hidden",
        display: "flex"
      }}
      gap="16px"
    >
      <Stack direction="column" gap="16px" sx={{ flexShrink: 0 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Breadcrumbs links={breadcrumbLinks} />
          {currentFolder && (
            <Share
              currentFolder={currentFolder}
              shared_with={currentFolder.shared_with || []}
              details={details || []}
              is_private={currentFolder.is_private || false}
              title={currentFolder.title || ""}
            />
          )}
        </Stack>

        <Stack direction="row" alignItems="center" gap="23px" sx={{ width: "100%" }}>
          <Stack direction="row" alignItems="center" gap="10px" sx={{ flex: 1, minWidth: 0 }}>
            <WorkspaceFolderCloseIcon sx={{ fill: "#0A3142", fontSize: 20 }} />
            <Typography
              noWrap
              sx={{
                color: "#0a3142",
                fontWeight: 700,
                fontSize: 16,
                lineHeight: "24px"
              }}
            >
              {folderTitle || ""}
            </Typography>
          </Stack>

          <Stack direction="row" alignItems="center" gap="8px" sx={{ flexShrink: 0 }}>
            <ColumnVisibilityMenu
              columnVisibility={columnVisibility}
              onToggle={toggleColumn}
              kind={kind as FolderKind}
              hideShareColumn={Boolean(!dealIdParam)}
            />
            {currentFolder?.id && (
              <FolderActions
                folder={currentFolder}
                refetchHandler={refetchFolderMetadata}
                useHook={useWorkspaceFolderHook}
              />
            )}
          </Stack>
        </Stack>

        <Stack direction="row" alignItems="center" gap="10px" justifyContent="space-between">
          <WorkspaceFilter
            isLoading={globalFolderFiltersLoading}
            handleChangeFilter={handleChangeFilter}
            filtersList={filtersList}
            filterTypes={filterTypes}
            applyMode
            state={activeFilters}
            isDataroom={isDataroom}
            isAggregateView={isAggregateView}
            dataroomOnlyKeys={dataroomOnlyKeys}
            defaultFilterKeys={["document_type", "file_type", "investor"]}
            skipDefaults={filtersUserInteracted}
          />
          <BulkActionsBar
            selectedItems={selectedItems}
            onClose={() => setSelectedItems([])}
            onView={handleBulkView}
            onExtract={handleBulkExtract}
            onDownload={handleBulkDownload}
            onChangeClassification={handleChangeClassification}
            onChangeCurrency={handleChangeCurrency}
            onChangeInvestor={handleChangeInvestor}
            onChangeEffectiveDate={handleChangeEffectiveDate}
            onChangeStatus={handleChangeStatus}
            onDelete={handleBulkDelete}
          />
        </Stack>
      </Stack>

      <Stack
        sx={{
          overflow: "hidden",
          flexGrow: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column"
        }}
      >
        {details?.length || isLoading || !!Object.keys(activeFilters).length ? (
          <MetadataTable
            kind={kind as FolderKind}
            data={details || []}
            isLoading={isLoading}
            columnVisibility={columnVisibility}
            columnOrder={columnOrder}
            selectedItems={selectedItems}
            setSelectedItems={setSelectedItems}
            reorderColumns={reorderColumns}
            refetchFolderMetadata={refetchFolderMetadata}
            onRowClick={handleRowClick}
            onFetchNextPage={handleFetchNextPage}
            hideShareColumn={Boolean(!dealIdParam)}
            FolderActionsComponent={WorkspaceFolderActions}
            FormActionsComponent={WorkspaceFormActions}
            DocumentActionsComponent={WorkspaceDocumentActions}
            FileActionsComponent={WorkspaceFileActions}
            useUpdateCellHookOverride={useWorkspaceCellHook}
            manualSorting
            onSortingChange={setSortBy}
            highlightedItemId={highlightedId}
          />
        ) : (
          <EmptyCard
            title={EMPTY_CARD_CONTENT.workspaceFolder.title}
            description={{
              text: EMPTY_CARD_CONTENT.workspaceFolder.description,
              externalText: EMPTY_CARD_CONTENT.workspaceFolder.externalDescription
            }}
            image={{
              src: EmptyCardDealWorkspace,
              sx: { width: 315, height: 140 }
            }}
            actionsElement={
              <DealRoleGuard
                availableRoles={[RoleName.Admin, RoleName.Analyst]}
                hideChild={!!noAccessPermission || !!isViewerPermission}
              >
                <Menu
                  hidden={currentFolder?.kind === "dataroom"}
                  icon={
                    <Button
                      sx={{ textTransform: "none", bgcolor: "#071F35" }}
                      startIcon={<AddIcon />}
                      variant="contained"
                    >
                      Add item
                    </Button>
                  }
                  keepTriggerEnabled
                  iconButtonProps={{ sx: { padding: 0, width: "auto" } }}
                  anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                  transformOrigin={{ vertical: "top", horizontal: "center" }}
                  options={[
                    {
                      title: "New folder",
                      icon: <WorkspaceFolderCloseIcon sx={{ fill: "#5d7782" }} />,
                      onClick: () => onCreateNewFolder?.(),
                      disabled: !onCreateNewFolder
                    },
                    {
                      title: "New document",
                      icon: <WorkspaceDocumentIcon sx={{ fill: "#5d7782", marginRight: "2px", padding: 0 }} />,
                      onClick: () => onAddDocument?.(),
                      disabled: !onAddDocument
                    },
                    {
                      title: "New form",
                      icon: <WorkspaceFormIcon sx={{ fill: "#5d7782", marginRight: "2px", padding: 0 }} />,
                      onClick: () => onAddForm?.(),
                      disabled: !onAddForm
                    },
                    {
                      title: "Upload file",
                      icon: <FileUploadOutlinedIcon sx={{ fill: "#5d7782", marginRight: "2px", padding: 0 }} />,
                      onClick: () => onFileUpload?.(),
                      disabled: !onFileUpload
                    }
                  ]}
                />
              </DealRoleGuard>
            }
          />
        )}
      </Stack>
    </Stack>
  );
};
